import { useCallback, useEffect, useRef, useState } from 'react'
import { admin, type PageRequestItem } from '../api/client'
import '../styles/widget.css'

/*
  Leaving a change request on the page you are looking at.

  Ported from the floating widget on cpCenter2. It shows only to somebody holding an admin
  cookie, on every page of the site, and it does one thing: box a bit of the page, say what
  should change about it, and put that in the queue for whoever works it next.

  Two things come back from a selection and they do different jobs.

    target  the CSS selector of the element under the box, plus the text inside it. This is
            what makes the request actionable later: it is how the block gets found in the
            source, when "the bit under the pricing table" would not.
    shot    a cropped JPEG of the region, for everything the words cannot carry, which is
            most design feedback.

  Pixels come from getDisplayMedia rather than from painting the DOM onto a canvas.
  Redrawing the DOM means reimplementing CSS, and it gets web fonts, gradients and shadows
  wrong. A real frame is what the admin actually saw. The cost is one browser share prompt,
  which is why the stream is opened once and reused until the panel closes.

  Without getDisplayMedia, or if the prompt is dismissed, selection still runs and still
  records the target. The request is worth having without the picture.
*/

/* Never let the widget's own chrome end up in a selector for the page underneath. */
const OURS = 'rq-'
const BLOCK = /^(P|LI|H1|H2|H3|H4|H5|H6|TD|TH|FIGCAPTION|BLOCKQUOTE|DD|DT|SUMMARY|CAPTION|BUTTON|A)$/

export function RequestWidget() {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [items, setItems] = useState<PageRequestItem[]>([])

  const [target, setTarget] = useState('')
  const [shot, setShot] = useState('')          // base64 JPEG, no data: prefix
  const [picking, setPicking] = useState(false)

  const stream = useRef<MediaStream | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const page = window.location.pathname + window.location.search

  const load = useCallback(async () => {
    try { setItems((await admin.requests(undefined, page)).items) } catch { setItems([]) }
  }, [page])

  useEffect(() => { if (open) void load() }, [open, load])

  // The share prompt is per stream, not per capture, so it is held until the panel closes.
  useEffect(() => {
    if (open) return
    stream.current?.getTracks().forEach(t => { try { t.stop() } catch { /* already gone */ } })
    stream.current = null
  }, [open])

  const send = async () => {
    const body = text.trim()
    if (!body || busy) return
    setBusy(true)
    setNote(null)
    try {
      await admin.addRequest({ page, body, target: target || null, shot: shot || null })
      setText('')
      setTarget('')
      setShot('')
      setNote('Added to the queue.')
      await load()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {picking && (
        <Marquee
          stream={stream}
          onDone={(t, s, why) => {
            setPicking(false)
            setTarget(t)
            setShot(s)
            setOpen(true)
            setNote(why)
            setTimeout(() => box.current?.focus(), 0)
          }}
          onCancel={() => { setPicking(false); setOpen(true) }}
        />
      )}

      {!picking && (
        <button
          className={`rq-fab${open ? ' is-open' : ''}`}
          onClick={() => setOpen(o => !o)}
          aria-label="Page requests"
          title="Page requests"
        >
          {open ? '×' : items.filter(i => i.status === 'pending').length || '✎'}
        </button>
      )}

      {open && !picking && (
        <div className="rq-panel" role="dialog" aria-label="Leave a page request">
          <div className="rq-head">
            <span className="rq-title">This page</span>
            <code className="rq-path">{page}</code>
          </div>

          <button className={`rq-pick${target || shot ? ' has' : ''}`} onClick={() => { setOpen(false); setPicking(true) }}>
            {target || shot ? 'Marked. Pick again' : 'Mark an area'}
          </button>

          {(shot || target) && (
            <div className="rq-marked">
              {shot && <img src={`data:image/jpeg;base64,${shot}`} alt="" />}
              <code>{target || 'No element captured.'}</code>
              <button className="rq-clear" onClick={() => { setShot(''); setTarget('') }} aria-label="Clear">×</button>
            </div>
          )}

          <textarea
            ref={box}
            className="rq-text"
            rows={3}
            value={text}
            placeholder="What should change here?"
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send() }}
          />

          {note && <p className="rq-note">{note}</p>}

          <div className="rq-actions">
            <button className="rq-add" disabled={!text.trim() || busy} onClick={send}>
              {busy ? 'Adding' : 'Add to queue'}
            </button>
            <a className="rq-all" href="/admin/#requests">Open the queue</a>
          </div>

          {items.length > 0 && (
            <ul className="rq-list">
              {items.map(i => (
                <li key={i.id} className={`rq-item is-${i.status}`}>
                  <span className="rq-item-status">{i.status}</span>
                  <span className="rq-item-body">{i.body}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  )
}

/* ── Boxing a region ─────────────────────────────────────────────────────── */

function Marquee({
  stream, onDone, onCancel,
}: {
  stream: React.MutableRefObject<MediaStream | null>
  onDone: (target: string, shot: string, note: string | null) => void
  onCancel: () => void
}) {
  const [rect, setRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  const from = useRef<{ x: number; y: number } | null>(null)
  const shade = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  // Asked for once, before the first drag, so the prompt is not sitting over the thing
  // being selected.
  useEffect(() => { void ensureStream(stream) }, [stream])

  const finish = async (x: number, y: number) => {
    const start = from.current
    from.current = null
    if (!start) return

    const r = {
      left: Math.min(start.x, x),
      top: Math.min(start.y, y),
      width: Math.abs(x - start.x),
      height: Math.abs(y - start.y),
    }
    setRect(null)
    if (r.width < 8 || r.height < 8) { onCancel(); return }

    /*
      Hidden synchronously, before the hit test, and not by asking React to unmount it.
      A state change does not take effect until the next render, so elementFromPoint was
      returning the overlay itself and the recorded target came back as the widget's own
      instruction text rather than anything on the page.
    */
    const overlay = shade.current
    if (overlay) overlay.style.visibility = 'hidden'

    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2)
    const block = hit && !isOurs(hit) ? blockFor(hit) : null
    const snippet = block ? (block.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 160) : ''
    const target = block ? selectorFor(block) + (snippet ? ` :: ${snippet}` : '') : ''

    const s = stream.current
    if (!s) { onDone(target, '', 'Screen capture was unavailable, so only the element was recorded.'); return }

    try {
      const shot = await cropFrom(s, r)
      onDone(target, shot, shot ? 'Area marked. Say what should change about it.' : null)
    } catch {
      onDone(target, '', 'Could not capture the image, but the element was recorded.')
    }
  }

  return (
    <div
      className="rq-shade"
      ref={shade}
      onMouseDown={e => { from.current = { x: e.clientX, y: e.clientY }; setRect({ x: e.clientX, y: e.clientY, w: 0, h: 0 }) }}
      onMouseMove={e => {
        if (!from.current) return
        setRect({
          x: Math.min(from.current.x, e.clientX),
          y: Math.min(from.current.y, e.clientY),
          w: Math.abs(e.clientX - from.current.x),
          h: Math.abs(e.clientY - from.current.y),
        })
      }}
      onMouseUp={e => { void finish(e.clientX, e.clientY) }}
    >
      <p className="rq-tip">Drag a box around what you are talking about. Escape to cancel.</p>
      {rect && (
        <div
          className="rq-marquee"
          style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
        />
      )}
    </div>
  )
}

async function ensureStream(ref: React.MutableRefObject<MediaStream | null>) {
  if (ref.current?.active) return ref.current
  if (!navigator.mediaDevices?.getDisplayMedia) return null

  try {
    const s = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      // Non-standard, Chromium only, and the reason this is one prompt rather than a
      // picker every time.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as MediaStreamConstraints)

    ref.current = s
    s.getVideoTracks()[0]?.addEventListener('ended', () => { ref.current = null })
    return s
  } catch {
    return null
  }
}

async function cropFrom(stream: MediaStream, r: { left: number; top: number; width: number; height: number }) {
  const video = document.createElement('video')
  video.srcObject = stream
  video.muted = true
  video.playsInline = true

  /*
    In the document, not detached. A video that is not in the tree is not guaranteed to
    decode in Chromium: play() still resolves and videoWidth still reports the right size,
    so every check passes and drawImage paints a black rectangle. One pixel, invisible, and
    removed again below.
  */
  video.style.cssText =
    'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none'
  document.body.appendChild(video)

  const cleanUp = () => {
    try { video.pause(); video.srcObject = null; video.remove() } catch { /* already gone */ }
  }

  try {
    await new Promise<void>((resolve, reject) => {
      const done = () => resolve()
      video.onerror = () => reject(new Error('video'))
      video.onloadedmetadata = () => video.play().then(() => {
        /*
          requestVideoFrameCallback fires only once a frame is actually available to
          present, which is the thing two animation frames were standing in for and did not
          reliably deliver. rAF counts paints of this document; the capture stream is a
          separate pipeline and can easily have produced nothing by the second tick.
        */
        if ('requestVideoFrameCallback' in video) {
          // A second frame after the first, so the overlay is certainly gone from it.
          video.requestVideoFrameCallback(() => video.requestVideoFrameCallback(done))
        } else {
          requestAnimationFrame(() => requestAnimationFrame(done))
        }
        // A stream that never yields a frame must not hang the capture forever.
        setTimeout(done, 1500)
      }).catch(reject)
    })
  } catch {
    cleanUp()
    return ''
  }

  const scale = video.videoWidth / window.innerWidth   // tab capture: frame maps to viewport
  const pad = 8
  const sx = Math.max(0, (r.left - pad) * scale)
  const sy = Math.max(0, (r.top - pad) * scale)
  const sw = Math.min(video.videoWidth - sx, (r.width + pad * 2) * scale)
  const sh = Math.min(video.videoHeight - sy, (r.height + pad * 2) * scale)

  if (sw < 4 || sh < 4) { cleanUp(); return '' }

  // Capped so the POST stays small.
  const k = Math.min(sw, 1400) / sw
  const c = document.createElement('canvas')
  c.width = Math.round(sw * k)
  c.height = Math.round(sh * k)
  const ctx = c.getContext('2d')
  ctx?.drawImage(video, sx, sy, sw, sh, 0, 0, c.width, c.height)
  cleanUp()

  // A frame that never arrived draws as flat black, and a black rectangle in the queue is
  // worse than no picture: it looks like the page rather than like a failure. Drop it and
  // let the note stand on its own, which is what happens when the prompt is dismissed.
  if (!ctx || isBlank(ctx, c.width, c.height)) return ''

  return c.toDataURL('image/jpeg', 0.82).replace(/^data:image\/jpeg;base64,/, '')
}

/*
  True when every sampled pixel is the same colour. Sampling a grid rather than the whole
  bitmap keeps this cheap on a large crop, and a genuinely uniform crop is not worth
  keeping either.
*/
function isBlank(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  const step = Math.max(1, Math.floor(Math.min(w, h) / 16))
  let first: string | null = null
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const [r, g, b] = ctx.getImageData(x, y, 1, 1).data
      const px = `${r},${g},${b}`
      if (first === null) first = px
      else if (px !== first) return false
    }
  }
  return true
}

/* Never describe our own chrome as though it were part of the page. */
function isOurs(el: Element): boolean {
  return !!el.closest('.rq-shade, .rq-panel, .rq-fab')
}

/*
  Climbs to the nearest element that actually holds readable copy, so the selector names a
  paragraph rather than a bare <span> inside one.
*/
function blockFor(el: Element): Element {
  let n: Element | null = el
  for (let hops = 0; n && n !== document.body && hops < 8; hops++) {
    if (BLOCK.test(n.tagName)) return n
    n = n.parentElement
  }
  return el
}

function selectorFor(el: Element): string {
  if (!el || el === document.body) return 'body'

  const parts: string[] = []
  let node: Element | null = el

  for (let depth = 0; node && node.nodeType === 1 && node !== document.body && depth < 6; depth++) {
    if (node.id) { parts.unshift(`#${node.id}`); break }

    let part = node.tagName.toLowerCase()
    // State classes are dropped along with our own. `is-in` is added by the reveal
    // observer once the element has been scrolled past, so a selector carrying it stops
    // matching the moment somebody looks at the page from the top.
    const classes = (node.getAttribute('class') || '')
      .trim().split(/\s+/)
      .filter(c => c && !c.startsWith(OURS) && !c.startsWith('is-') && !c.startsWith('has-'))
    if (classes.length) part += `.${classes.slice(0, 2).join('.')}`

    const parent: HTMLElement | null = node.parentElement
    if (parent) {
      const same = Array.from(parent.children).filter(c => c.tagName === node!.tagName)
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(node) + 1})`
    }

    parts.unshift(part)
    node = parent
  }

  return parts.join(' > ')
}
